import React, { Fragment, ReactElement, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ControlPointOutlinedIcon from '@material-ui/icons/ControlPointOutlined';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';

import ConfirmationModal from '../../../layout/ConfirmationModal';
import { displayDestination, displayDate } from '../../../../utils';
import { APIPickTour } from '../../../../redux/sagas/types';
import { resetActivePickTour } from '../../../../redux/actions/orders.actions';
import { PICK_TOUR_SUB_STATUS_TIMEOUT } from '../../../../constants';

import styles from './ResetPickTour.css';

type Props = {
  pickTour: APIPickTour;
  isDisabled: boolean;
  isSelected: boolean;
  pickTourIndex: number;
  onSelect: () => void;
  resumePickTour: (pickTour: APIPickTour) => void;
};

type ModalState = {
  title: string;
  message: string;
  buttonTitle: string;
} | null;

const PickTourItem = ({
  pickTour,
  isSelected,
  isDisabled,
  onSelect,
  resumePickTour,
  pickTourIndex,
}: Props): ReactElement => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const translationRoot = 'RESUME_PICK_TOURS';
  const [modalState, setModalState] = useState<ModalState>(null);
  const { metadata, createTS, assignedPickerEmployeeName, pickTourType, pickTourSubStatus } = pickTour;
  const isPickTourResumable = pickTourSubStatus === PICK_TOUR_SUB_STATUS_TIMEOUT;

  function determinePickTourType() {
    let destination = '';
    if (metadata.ordersSummaryData.length > 0 && metadata.ordersSummaryData[0].destination) {
      destination = metadata.ordersSummaryData[0].destination;
      destination =
        String(Number(destination)) === 'NaN'
          ? ` | ${destination}`
          : ` | ${String(Number(destination)).padStart(5, '0')}`;
    }
    return `${t('ORDER_LIST.LIST_ITEM.RESUME')}${destination}`;
  }

  const resetResumePickTour = (event?: React.MouseEvent) => {
    const actionKey = isPickTourResumable ? 'RESUME' : 'RESET';
    setModalState({
      title: t(isPickTourResumable ? `${translationRoot}.RESUME_PICK_TOUR` : 'WAFFLE_MENU.RESET_PICK_TOUR'),
      message: t(`${translationRoot}.${actionKey}_TOUR_CONFIRMATION`),
      buttonTitle: t(`${translationRoot}.${actionKey}`),
    });

    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  const handleModalClose = () => {
    setModalState(null);
  };

  const handleSubmit = () => {
    if (isPickTourResumable) {
      resumePickTour(pickTour);
    } else {
      dispatch(
        resetActivePickTour({
          pickTour: {
            ...pickTour,
            pickTourSubStatus: PICK_TOUR_SUB_STATUS_TIMEOUT,
          },
          pickTourIndex,
        })
      );
    }
    handleModalClose();
  };

  return (
    <Fragment>
      <li className={styles.li} onClick={onSelect}>
        <div className={styles.pickInfo}>
          {isSelected ? <RemoveCircleOutlineIcon /> : <ControlPointOutlinedIcon />}
          <div className={styles.mainArea}>
            <div className={styles.destination} data-cy="pickTour-destination">
              {determinePickTourType()}
            </div>
            <div className={styles.content}>
              <div className={styles.itemsPick}>
                <span>{t(`${translationRoot}.ACTIVE_PICK_ORDERS`)}</span>
                <span className={styles.quantity}>{metadata.ordersSummaryData.length}</span>
              </div>
              <div>
                <span>{t(`${translationRoot}.STARTED_AT`)}</span>
                <span className={styles.pickTourTime}>{displayDate({ date: createTS }).split('-')[1]}</span>
              </div>
              <div>
                {assignedPickerEmployeeName && <div className={styles.employeeName}>{assignedPickerEmployeeName}</div>}
              </div>
              <div className={styles.resetResumeTour}>
                <button
                  disabled={isDisabled}
                  type="button"
                  onClick={resetResumePickTour}
                  aria-label={t('WAFFLE_MENU.RESET_PICK_TOUR')}
                >
                  {t(`${isPickTourResumable ? `${translationRoot}.RESUME_PICK_TOUR` : 'WAFFLE_MENU.RESET_PICK_TOUR'}`)}
                </button>
              </div>
            </div>
          </div>
        </div>
        {isSelected && (
          <div className={styles.pickOrdersWrapper}>
            {metadata.ordersSummaryData.map(order => (
              <div key={order.orderID} className={styles.pickOrders}>
                <span className={styles.pickOrderDestination}>
                  {displayDestination({ orderType: pickTourType, destination: order.destination })}
                </span>
                <span className={styles.textLight}>{t('ORDER_LIST.LIST_ITEM.QTY')}</span>
                <span className={styles.textLight}>{order.qty}</span>
              </div>
            ))}
          </div>
        )}
      </li>
      {modalState && (
        <ConfirmationModal
          title={modalState.title}
          message={modalState.message}
          buttonTitle={modalState.buttonTitle}
          onModalClose={handleModalClose}
          handleSubmit={handleSubmit}
        />
      )}
    </Fragment>
  );
};

export default PickTourItem;
